/* Staten Tools frame engraving preview: shows the customer's text/logo artwork engraved on the
   front upright arm (one side only) in place of the branding decal, on the real product photo.
   API: window.stFramePreview.render(root, flatCanvas, {size, ready})
        window.stFramePreview.export(root) -> Promise<{file, placement} | null>
   Calibration: photo normalised to 1000px width (y down). `quad` = branding decal outline on the
   front arm, corners TL, TR, BR, BL as seen in the photo (verified by drawing the outlines onto the
   local sources in attached_assets/sheave-preview-sources with ImageMagick).
   Only pixels inside the slightly expanded decal quad are repainted, using a gradient sampled from
   bare paint on the same arm just above and below the decal. Hook, hardware, safety labels, wheel
   and frame outline are never touched. */
(() => {
  'use strict';
  if (window.stFramePreview) return;
  const SHEAVE = window.stSheavePreview?.calibration;
  const BASE = 'https://cdn.shopify.com/s/files/1/0849/5130/2451/files/';
  const CAL = {
    '6':  {src: SHEAVE?.['6']?.src  || BASE + '6_inch_Cable_Puller_Sheave.jpg?v=1755896044', quad: [[332, 392], [418, 389], [413, 738], [326, 734]], grow: 2},
    '12': {src: BASE + 'st-frame-12-blank.png?v=1791051409', blank: true, quad: [[344, 414], [416, 407], [410, 933], [335, 938]], grow: 0},
    '18': {src: SHEAVE?.['18']?.src || BASE + 'Mainimage2_2.jpg?v=1773679354',               quad: [[551, 274], [610, 271], [605, 541], [548, 544]], grow: 3.5},
    '24': {src: SHEAVE?.['24']?.src || BASE + 'Mainimage2_3.jpg?v=1773679882',               quad: [[575, 205], [619, 201], [619, 423], [577, 426]], grow: 3}
  };
  // A combined preview must use the wheel's photograph and matching frame
  // coordinates, not the differently cropped 12-inch frame-only reference.
  CAL['12-combined'] = {src: SHEAVE?.['12']?.src || BASE + '12_1.jpg?v=1758634901',
    quad: [[355, 316], [414, 312], [414, 558], [352, 570]], grow: 2.5};
  // Merchant-supplied reference, explicitly labelled rather than presented as a
  // size-specific product photo. Only replace the branding on the white tube.
  for (const size of ['3', '4', '5', '6']) CAL[`feed-${size}`] = {
    src: BASE + 'st-feeding-frame-reference.png?v=1791052268',
    quad: [[557, 319], [792, 325], [792, 402], [557, 395]],
    grow: 2, feeding: true, horizontal: true
  };
  const NOTE = 'Fit-to-arm illustration. Final frame engraving dimensions are confirmed by our team.';
  const states = new WeakMap();
  const imgCache = new Map();

  function loadImg(size) {
    if (imgCache.has(size)) return imgCache.get(size);
    const p = new Promise((res, rej) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => res(im);
      im.onerror = () => rej(Error(`The ${size} inch frame photo could not be loaded.`));
      im.src = CAL[size].src;
    });
    p.catch(() => imgCache.delete(size));
    imgCache.set(size, p);
    return p;
  }

  function ensureUI(root) {
    let st = states.get(root);
    if (st && st.el.isConnected) return st;
    const el = document.createElement('div');
    el.className = 'st-frame';
    el.setAttribute('data-st-frame-preview', '');
    el.hidden = true;
    el.innerHTML = '<div class="st-frame__head"><span class="st-frame__title">Frame placement preview — one side</span><span class="st-frame__size" data-fp-size></span></div>' +
      '<div class="st-frame__stage" data-fp-stage><canvas class="st-frame__canvas" role="img" aria-label="Frame engraving photo preview"></canvas></div>' +
      '<button class="st-frame__expand" type="button" data-fp-expand disabled>Expand preview</button>' +
      '<p class="st-frame__status" role="status" aria-live="polite" data-fp-status></p>' +
      '<p class="st-frame__note">' + NOTE + '</p>';
    const host = root.querySelector('[data-eng-preview]');
    if (host) host.appendChild(el); else root.append(el);
    st = {el, canvas: el.querySelector('canvas'), token: 0, pending: Promise.resolve(), result: null, error: null, size: ''};
    Object.assign(st.canvas.style, {display: 'block', width: '100%', height: 'auto', maxWidth: '100%', maxHeight: 'min(420px, 60vh)', objectFit: 'contain'});
    states.set(root, st);
    el.querySelector('[data-fp-expand]').addEventListener('click', async event => {
      event.preventDefault();
      event.stopPropagation();
      await st.pending;
      if (st.error || !st.result) return;
      try {
        if (!window.stEngravingExpand) throw Error('The expanded viewer is still loading. Please refresh and try again.');
        window.stEngravingExpand.open(st.canvas);
      } catch (e) { status(st, 'error', e.message || 'Unable to expand this preview.'); }
    });
    return st;
  }

  function status(st, tone, text) {
    const s = st.el.querySelector('[data-fp-status]');
    s.textContent = text; s.dataset.tone = tone;
    st.el.querySelector('[data-fp-stage]').dataset.busy = String(tone === 'loading');
    st.el.querySelector('[data-fp-expand]').disabled = tone === 'loading' || tone === 'error';
  }

  // Affine-map source triangle to destination triangle, clipped (same formula as st-sheave-preview.js).
  function tri(ctx, img, s0, s1, s2, d0, d1, d2) {
    const ex = (d0[0] + d1[0] + d2[0]) / 3, ey = (d0[1] + d1[1] + d2[1]) / 3;
    const g = p => { const dx = p[0] - ex, dy = p[1] - ey, l = Math.hypot(dx, dy) || 1; return [p[0] + dx / l * 0.6, p[1] + dy / l * 0.6]; };
    const a = g(d0), b = g(d1), c = g(d2);
    ctx.save();
    ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c[0], c[1]); ctx.closePath(); ctx.clip();
    const sx1 = s1[0] - s0[0], sy1 = s1[1] - s0[1];
    const sx2 = s2[0] - s0[0], sy2 = s2[1] - s0[1];
    const dx1 = d1[0] - d0[0], dy1 = d1[1] - d0[1];
    const dx2 = d2[0] - d0[0], dy2 = d2[1] - d0[1];
    const den = sx1 * sy2 - sx2 * sy1;
    if (Math.abs(den) < 1e-9) { ctx.restore(); return; }
    const m11 = (dx1 * sy2 - dx2 * sy1) / den;
    const m12 = (dy1 * sy2 - dy2 * sy1) / den;
    const m21 = (dx2 * sx1 - dx1 * sx2) / den;
    const m22 = (dy2 * sx1 - dy1 * sx2) / den;
    const dx = d0[0] - m11 * s0[0] - m21 * s0[1];
    const dy = d0[1] - m12 * s0[0] - m22 * s0[1];
    ctx.transform(m11, m12, m21, m22, dx, dy);
    ctx.drawImage(img, 0, 0);
    ctx.restore();
  }

  const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  // s = 0 top .. 1 bottom of arm, t = 0 left .. 1 right edge.
  const quadPt = (q, s, t) => lerp(lerp(q[0], q[1], t), lerp(q[3], q[2], t), s);

  function grow(q, d) {
    const cx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, cy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
    return q.map(([x, y]) => { const l = Math.hypot(x - cx, y - cy) || 1; return [x + (x - cx) / l * d, y + (y - cy) / l * d]; });
  }

  // Warp a source canvas (whose x -> t, y -> s, or rotated for artwork) into a quad region.
  function warpGrid(ctx, src, map, NX, NY) {
    const W = src.width, H = src.height;
    for (let i = 0; i < NX; i++) for (let j = 0; j < NY; j++) {
      const u0 = i / NX, u1 = (i + 1) / NX, v0 = j / NY, v1 = (j + 1) / NY;
      const sA = [u0 * W, v0 * H], sB = [u1 * W, v0 * H], sC = [u1 * W, v1 * H], sD = [u0 * W, v1 * H];
      const dA = map(u0, v0), dB = map(u1, v0), dC = map(u1, v1), dD = map(u0, v1);
      tri(ctx, src, sA, sB, sC, dA, dB, dC);
      tri(ctx, src, sA, sC, sD, dA, dC, dD);
    }
  }

  // Build a paint patch: columns across the arm sampled just above and just below the decal,
  // blended vertically. Returns a canvas of NU x NV pixels (x across arm, y down arm).
  function paintPatch(ctx, k, q) {
    const NU = 12, NV = 24, off = 7;
    const sample = (s, t) => {
      const p = quadPt(q, s, t), [x, y] = [Math.round(p[0] * k), Math.round(p[1] * k)], r = Math.max(1, Math.round(1.5 * k));
      const d = ctx.getImageData(x - r, y - r, 2 * r + 1, 2 * r + 1).data;
      let R = 0, G = 0, B = 0, n = 0;
      for (let i = 0; i < d.length; i += 4) { R += d[i]; G += d[i + 1]; B += d[i + 2]; n++; }
      return [R / n, G / n, B / n];
    };
    const hT = Math.hypot(q[3][0] - q[0][0], q[3][1] - q[0][1]);
    const sTop = -off / hT, sBot = 1 + off / hT;
    const top = [], bot = [];
    for (let i = 0; i < NU; i++) { const t = 0.12 + 0.76 * i / (NU - 1); top.push(sample(sTop, t)); bot.push(sample(sBot, t)); }
    const cv = document.createElement('canvas'); cv.width = NU; cv.height = NV;
    const pc = cv.getContext('2d'), id = pc.createImageData(NU, NV);
    for (let j = 0; j < NV; j++) for (let i = 0; i < NU; i++) {
      const f = j / (NV - 1), o = (j * NU + i) * 4, n = (Math.random() - 0.5) * 3;
      for (let c = 0; c < 3; c++) id.data[o + c] = top[i][c] * (1 - f) + bot[i][c] * f + n;
      id.data[o + 3] = 255;
    }
    pc.putImageData(id, 0, 0);
    return cv;
  }

  // Snapshot artwork and trim transparent / near-white margins so it reads at frame scale.
  function snapshotTrim(art) {
    const w = Math.min(1600, art.width), h = Math.max(1, Math.round(art.height * w / art.width));
    const snap = document.createElement('canvas'); snap.width = w; snap.height = h;
    const sc = snap.getContext('2d'); sc.drawImage(art, 0, 0, w, h);
    const d = sc.getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const o = (y * w + x) * 4;
      if (d[o + 3] > 16 && (d[o] + d[o + 1] + d[o + 2]) < 690) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) return null;
    const pad = 2; x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
    const out = document.createElement('canvas'); out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
    out.getContext('2d').drawImage(snap, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  // Fit rotated art (reads top-to-bottom, letter tops toward the right edge) inside the decal quad.
  function fit(q, art, horizontal = false) {
    const height = (Math.hypot(q[3][0] - q[0][0], q[3][1] - q[0][1]) + Math.hypot(q[2][0] - q[1][0], q[2][1] - q[1][1])) / 2;
    const width = (Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]) + Math.hypot(q[2][0] - q[3][0], q[2][1] - q[3][1])) / 2;
    const L = horizontal ? width : height, Wd = horizontal ? height : width;
    const availL = L * 0.9, availW = Wd * 0.78;
    const scale = Math.min(availL / art.width, availW / art.height);
    const ds = art.width * scale / L, dt = art.height * scale / Wd;
    return {s0: (1 - ds) / 2, ds, t0: (1 - dt) / 2, dt};
  }

  async function doRender(root, st, token, art, o) {
    const size = String(o.size || '');
    st.size = size; st.result = null; st.error = null;
    if (!CAL[size]) { st.el.hidden = true; return; }
    st.el.hidden = false;
    // Snapshot artwork synchronously so later edits to the flat canvas cannot leak into this render.
    const trimmed = (o.ready && art && art.width && art.height) ? snapshotTrim(art) : null;
    const photoKey = size === '12' && o.combined ? '12-combined' : size;
    const c = CAL[photoKey];
    st.el.querySelector('.st-frame__title').textContent = o.combined
      ? 'Wheel + frame engraving preview' : 'Frame placement preview — one side';
    st.el.querySelector('[data-fp-size]').textContent = c.feeding ? `${size.slice(5)}" feeding sheave` : `${size}" sheave frame`;
    st.el.querySelector('.st-frame__note').textContent = c.feeding
      ? 'Representative feeding-sheave reference photo. One frame/tube side only; final engraving dimensions and placement confirmed by our team.'
      : NOTE;
    status(st, 'loading', 'Loading product photo...');
    let img;
    try { img = await loadImg(photoKey); }
    catch (e) { if (token === st.token) { st.error = e; status(st, 'error', e.message + ' Check your connection and try again.'); } return; }
    if (token !== st.token || !root.isConnected) return;
    const cv = st.canvas;
    cv.width = img.naturalWidth; cv.height = img.naturalHeight;
    cv.style.aspectRatio = `${cv.width} / ${cv.height}`;
    const ctx = cv.getContext('2d', {willReadFrequently: true});
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0);
    if (o.combined && o.ready) {
      // Wait for the wheel's own stable export rather than copying a stale
      // canvas while its asynchronous photo is still loading.
      const wheel = await window.stSheavePreview.export(root);
      if (!wheel?.file) throw Error('The wheel preview is not ready.');
      const bitmap = await createImageBitmap(wheel.file);
      if (token !== st.token || !root.isConnected) { bitmap.close(); return; }
      ctx.drawImage(bitmap, 0, 0, cv.width, cv.height);
      bitmap.close();
    }
    const k = cv.width / 1000, q = c.quad, mq = grow(q, c.grow);
    // Repaint only the decal area with paint sampled from the same arm.
    if (!c.blank) {
    const patch = paintPatch(ctx, k, q);
    ctx.save(); ctx.scale(k, k);
    ctx.beginPath(); mq.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])); ctx.closePath(); ctx.clip();
    ctx.imageSmoothingEnabled = true;
    warpGrid(ctx, patch, (u, v) => quadPt(grow(q, c.grow + 1.5), v, u), 4, 8);
    ctx.restore();
    }
    let fitR = null;
    const ready = !!trimmed;
    if (ready) {
      fitR = fit(q, trimmed, c.horizontal);
      const layer = document.createElement('canvas'); layer.width = cv.width; layer.height = cv.height;
      const lc = layer.getContext('2d'); lc.imageSmoothingEnabled = true; lc.imageSmoothingQuality = 'high';
      lc.scale(k, k);
      // art x (u) runs down the arm; art y (v) = 0 at the right edge (letter tops face right).
      warpGrid(lc, trimmed, (u, v) => c.horizontal
        ? quadPt(q, fitR.t0 + v * fitR.dt, fitR.s0 + u * fitR.ds)
        : quadPt(q, fitR.s0 + u * fitR.ds, fitR.t0 + (1 - v) * fitR.dt), 24, 4);
      ctx.save(); ctx.globalAlpha = 0.8; ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(layer, 0, 0); ctx.restore();
      cv.setAttribute('aria-label', c.feeding ? 'Feeding sheave reference photo with your engraving on one side of the white frame tube'
        : `Photo of the ${size} inch sheave with your engraving reading top to bottom along the front frame arm, in place of the branding decal`);
      status(st, 'ready', o.combined ? 'Your engraving shown on both the wheel and frame. Opposite or reverse sides are not visible in this photo.'
        : c.feeding ? 'Your artwork on one frame/tube side — illustrative reference preview.'
        : `Your artwork shown on the front frame arm of the ${size}" sheave (one side).`);
    } else {
      cv.setAttribute('aria-label', c.feeding ? 'Feeding sheave reference photo with the frame tube branding area blank'
        : `Photo of the ${size} inch sheave with the front arm decal area shown blank`);
      status(st, 'idle', 'Frame branding area shown blank. Add your text or artwork above to see it engraved.');
    }
    st.result = {size, ready, fit: fitR, photoKey, combined: !!o.combined};
  }

  function render(root, flatCanvas, opts = {}) {
    if (!root) return Promise.resolve();
    const st = ensureUI(root);
    const token = ++st.token;
    st.pending = doRender(root, st, token, flatCanvas, opts).catch(e => {
      if (token === st.token) { st.error = e; status(st, 'error', 'Frame preview failed: ' + (e?.message || 'unknown error')); }
    });
    return st.pending;
  }

  async function exportPreview(root) {
    const st = states.get(root);
    if (!st) return null;
    let t;
    do { t = st.token; await st.pending; } while (t !== st.token);
    if (!CAL[st.size]) return null;
    if (st.error || !st.result?.ready) throw Error(st.error?.message || 'The frame engraving preview is not ready. Try again.');
    const blob = await new Promise((res, rej) => {
      try { st.canvas.toBlob(b => b ? res(b) : rej(Error('Could not create the frame preview image.')), 'image/png'); }
      catch (e) { rej(Error('The frame photo could not be exported (cross-origin blocked). Refresh and try again.')); }
    });
    const r = st.result, c = CAL[r.photoKey || r.size];
    const placement = {v: 2, size: r.size, ready: r.ready,
      location: c.feeding ? 'feeding sheave white frame tube, one side, in place of branding' : 'front upright frame arm, one side, in place of branding decal',
      orientation: c.horizontal ? 'reads left to right along tube' : 'reads top to bottom along arm',
      referencePhoto: !!c.feeding, includesWheelPreview: r.combined, illustrative: true, requiresTeamDimensionConfirmation: true, photo: c.src,
      decalQuad1000: c.quad, ...(r.fit ? {fitAlongArm: [+r.fit.s0.toFixed(3), +(r.fit.s0 + r.fit.ds).toFixed(3)], fitAcrossArm: [+r.fit.t0.toFixed(3), +(r.fit.t0 + r.fit.dt).toFixed(3)]} : {})};
    return {file: new File([blob], `frame-${r.size}in-preview.png`, {type: 'image/png'}), placement};
  }

  window.stFramePreview = {render, export: exportPreview, calibration: CAL, version: 1};
})();
